/*
========================================
PRODUCT PREVIEW

MAIN CONSTRUCTOR

Главный файл-сборщик блока preview.

Отвечает за:
- сборку всех preview-блоков
- общее состояние
- active image
- состояние левой и правой шторки
- передачу данных в дочерние блоки

Не содержит:
- разметку header
- разметку центральной картинки
- разметку шторок
- разметку цены и корзины

Все визуальные части вынесены отдельно.
========================================
*/

"use client"

import { useMemo, useRef, useState } from "react"
import { addToCart } from "@lib/data/cart"

import ProductPreviewHeader from "./blocks/product-preview-header"
import ProductPreviewMainImage from "./blocks/product-preview-main-image"
import ProductPreviewSidePanels from "./blocks/product-preview-side-panels"
import ProductPreviewPriceCart from "./blocks/product-preview-price-cart"
import ProductPreviewQtyModal from "./blocks/product-preview-qty-modal"

import { productPreviewKeyframes } from "./animations/product-preview-animations"
import {
  getProductPreviewText,
  PRODUCT_PREVIEW_HANDLE_WIDTH,
} from "./config/product-preview-config"

type ProductImageStripProps = {
  thumbnail?: string | null
  images?: { id?: string | null; url?: string | null }[] | null
  title?: string | null
  description?: string | null
  sku?: string | null
  productHandle?: string | null
  locale?: string | null
  price?: string | null
  originalPrice?: string | null
  variantId?: string | null
  variants?: {
    id: string
    title?: string | null
  }[] | null
  countryCode?: string | null
}

export default function ProductImageStrip({
  thumbnail,
  images,
  title,
  sku,
  description,
  productHandle,
  locale,
  price,
  originalPrice,
  variantId,
  variants,
  countryCode,
}: ProductImageStripProps) {

  const gallery = useMemo(() => {
    const raw = [
      thumbnail ? { url: thumbnail } : null,
      ...(images || []).map((img) => ({ url: img?.url || null })),
    ].filter(Boolean) as { url: string | null }[]

    const seen = new Set<string>()

    return raw.filter((img) => {
      if (!img.url || seen.has(img.url)) return false
      seen.add(img.url)
      return true
    })
  }, [thumbnail, images])

  const [activeIndex, setActiveIndex] = useState(0)
  const [leftPanelOpen, setLeftPanelOpen] = useState(false)
  const [rightPanelOpen, setRightPanelOpen] = useState(false)
  const [imageModalOpen, setImageModalOpen] = useState(false)

  const [qtyModalOpen, setQtyModalOpen] = useState(false)
  const [selectedQty, setSelectedQty] = useState(1)
  const [isAddingToCart, setIsAddingToCart] = useState(false)

  const [selectedVariantId, setSelectedVariantId] = useState(variantId || null)

  const text = getProductPreviewText(locale)

  const startX = useRef<number | null>(null)
  const startY = useRef<number | null>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null || startY.current === null) return

    const endX = e.changedTouches[0].clientX
    const endY = e.changedTouches[0].clientY

    const diffX = endX - startX.current
    const diffY = endY - startY.current

    const absX = Math.abs(diffX)
    const absY = Math.abs(diffY)

    const screenWidth = window.innerWidth
    const edgeZone = screenWidth * 0.5

    const startedFromLeftEdge = startX.current <= edgeZone
    const startedFromRightEdge = startX.current >= screenWidth - edgeZone

    if (absX < 60 || absX <= absY) {
      startX.current = null
      startY.current = null
      return
    }

    if (leftPanelOpen) {
      if (diffX < -60) setLeftPanelOpen(false)
      startX.current = null
      startY.current = null
      return
    }

    if (rightPanelOpen) {
      if (diffX > 60) setRightPanelOpen(false)
      startX.current = null
      startY.current = null
      return
    }

    if (startedFromLeftEdge && diffX > 60) setLeftPanelOpen(true)
    if (startedFromRightEdge && diffX < -60) setRightPanelOpen(true)

    startX.current = null
    startY.current = null
  }

  const toggleLeftPanel = () => {
    if (rightPanelOpen || imageModalOpen) return
    setLeftPanelOpen((prev) => !prev)
  }

  const toggleRightPanel = () => {
    if (leftPanelOpen || imageModalOpen) return
    setRightPanelOpen((prev) => !prev)
  }

  if (!gallery.length) {
    return (
      <div className="w-full">
        <div className="aspect-[4/5] w-full rounded-2xl bg-neutral-800" />
      </div>
    )
  }

  const activeImage = gallery[activeIndex]?.url || gallery[0]?.url || ""

  const resolvedPrice = price || originalPrice || null
  const resolvedOriginalPrice = originalPrice || price || null

  const hasDiscount =
    !!originalPrice && !!price && originalPrice.trim() !== price.trim()

  const canAddToCart = !!variantId && !!countryCode

  const handleConfirmAddToCart = async () => {
  if (!canAddToCart || !selectedVariantId || !countryCode) {
    return
  }

  try {
    setIsAddingToCart(true)

    await addToCart({
  variantId: selectedVariantId,
      quantity: selectedQty,
      countryCode,
    })

    setQtyModalOpen(false)
    setSelectedQty(1)
  } catch (error) {
    console.error("Add to cart failed:", error)
  } finally {
    setIsAddingToCart(false)
  }
}

  return (
    <div className="w-full">
      <ProductPreviewHeader
        title={title}
        handle={productHandle}
        gallery={gallery}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
      />

      <div
        className="relative aspect-[4/5] w-full rounded-2xl"
        onTouchStart={imageModalOpen ? undefined : handleTouchStart}
        onTouchEnd={imageModalOpen ? undefined : handleTouchEnd}
      >
        <div className="absolute inset-0 rounded-2xl textured-mobile-bg" />

        <div className="relative z-10">
          <ProductPreviewMainImage
            activeImage={activeImage}
            gallery={gallery}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            title={title}
            isOpen={imageModalOpen}
            setIsOpen={setImageModalOpen}
          />
        </div>

        {!imageModalOpen && (
          <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-2xl">
            <ProductPreviewSidePanels
              leftPanelOpen={leftPanelOpen}
              rightPanelOpen={rightPanelOpen}
              toggleLeftPanel={toggleLeftPanel}
              toggleRightPanel={toggleRightPanel}
              handleTouchStart={handleTouchStart}
              handleTouchEnd={handleTouchEnd}
              title={title}
              description={description}
              sku={sku}
              text={text}
              HANDLE_WIDTH={PRODUCT_PREVIEW_HANDLE_WIDTH}
            />
          </div>
        )}
      </div>

      <ProductPreviewPriceCart
  addToCartLabel={text.addToCart}
  resolvedPrice={resolvedPrice}
  resolvedOriginalPrice={resolvedOriginalPrice}
  hasDiscount={hasDiscount}
  onAddClick={() => {
    setQtyModalOpen(true)
  }}
/>

      <ProductPreviewQtyModal
  open={qtyModalOpen}
  quantity={selectedQty}
  onDecrease={() => {
    setSelectedQty((prev) => Math.max(1, prev - 1))
  }}
  onIncrease={() => {
    setSelectedQty((prev) => prev + 1)
  }}
  onConfirm={handleConfirmAddToCart}
  onClose={() => {
    setQtyModalOpen(false)
  }}
  isAdding={isAddingToCart}

  variants={
  variants?.map((variant) => ({
    id: variant.id,
    title: variant.title || "Default option",
  })) || []
}

  selectedVariantId={selectedVariantId || undefined}
  onSelectVariant={setSelectedVariantId}
  variantLabel="Choose set"
/>
      <style jsx>{productPreviewKeyframes}</style>
    </div>
  )
}